document.documentElement.classList.remove('no-js');

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function initMenuDrawer() {
  const drawer = document.querySelector('[data-menu-drawer]');
  const openers = document.querySelectorAll('[data-menu-open]');
  // Two closers now: the drawer's own close button, and the scrim overlay
  // (a sibling of the drawer, not a descendant — hence document-wide query).
  const closers = document.querySelectorAll('[data-menu-close]');
  const firstCloser = drawer?.querySelector('[data-menu-close]');
  let previousFocus = null;

  if (!drawer || !openers.length || !closers.length) return;

  const close = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
    openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    previousFocus?.focus();
  };

  const open = (event) => {
    previousFocus = event.currentTarget;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
    openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    firstCloser?.focus();
  };

  openers.forEach((button) => button.addEventListener('click', open));
  closers.forEach((button) => button.addEventListener('click', close));

  document.addEventListener('keydown', (event) => {
    if (!drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') close();
    if (event.key !== 'Tab') return;

    const focusable = [...drawer.querySelectorAll(focusableSelector)];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

function formatMoney(cents, format = '${{amount}}') {
  const value = (Number(cents || 0) / 100).toFixed(2);
  const parts = value.split('.');
  const dollars = Number(parts[0]).toLocaleString('en-US');
  const amount = `${dollars}.${parts[1]}`;
  const amountNoDecimals = Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });

  return format
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount}}', amount)
    .replace('{{amount_with_comma_separator}}', amount.replaceAll(',', ' ').replace('.', ','));
}

function initProductForms() {
  // ISENZA's products carry a single option (Size) — see PRODUCT.md. Sizes
  // are rendered directly from product.variants (main-product.liquid), so
  // this reads variant_id radios rather than the generic multi-option
  // matching a color+size picker would need. Revisit if a second option
  // (e.g. color) is ever added.
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const variantsScript = root.querySelector('[data-product-variants]');
    const form = root.querySelector('[data-product-form]');
    if (!variantsScript || !form) return;

    const variants = JSON.parse(variantsScript.textContent);
    const idInput = form.querySelector('[name="id"]');
    const price = root.querySelector('[data-product-price]');
    const comparePrice = root.querySelector('[data-compare-price]');
    const submit = form.querySelector('[name="add"]');
    const inventory = root.querySelector('[data-inventory-note]');
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const lowThreshold = Number(root.dataset.lowThreshold || 8);

    // "Pre order" never changes text — availability is conveyed by which
    // sizes are selectable (disabled radios) and whether the button itself
    // is enabled, not by swapping its label.
    const renderVariant = () => {
      const checked = form.querySelector('[data-option-input]:checked');
      const variant = checked && variants.find((item) => String(item.id) === checked.value);

      if (!variant) {
        if (submit) submit.disabled = true;
        if (inventory) inventory.hidden = true;
        return;
      }

      idInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price, moneyFormat);

      if (comparePrice) {
        comparePrice.hidden = !variant.compare_at_price || variant.compare_at_price <= variant.price;
        comparePrice.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price, moneyFormat) : '';
      }

      if (submit) submit.disabled = !variant.available;

      if (inventory) {
        const tracked = variant.inventory_management && variant.inventory_policy === 'deny';
        const isLow = tracked && variant.inventory_quantity > 0 && variant.inventory_quantity <= lowThreshold;
        inventory.hidden = !isLow;
        inventory.textContent = isLow ? `Only ${variant.inventory_quantity} remaining` : '';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    form.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input]')) renderVariant();
    });

    // Deliberately no renderVariant() call here — the mock's size selector
    // starts with nothing chosen and the button disabled until the visitor
    // picks a size, even though Shopify would otherwise auto-select the
    // first available variant.
  });
}

function initCartLineActions() {
  const items = document.querySelectorAll('[data-cart-item]');
  if (!items.length) return;

  const form = document.querySelector('[data-cart-form]');
  const moneyFormat = form?.dataset.moneyFormat || '${{amount}}';

  // Applies a fresh Cart object (as returned by /cart/change.js) to the DOM —
  // never trusts locally-incremented numbers, always reflects what Shopify
  // actually persisted (e.g. a quantity clamped by low stock).
  const applyCartState = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => { el.textContent = formatMoney(cart.total_price, moneyFormat); });

    if (cart.item_count === 0) {
      // Simplest correct way to show the empty-cart state already built into
      // the Liquid template, rather than duplicating it in JS.
      window.location.reload();
      return;
    }

    document.querySelectorAll('[data-cart-item]').forEach((item) => {
      const line = cart.items.find((cartItem) => cartItem.key === item.dataset.lineKey);

      if (!line) {
        item.remove();
        return;
      }

      const input = item.querySelector('input[type="number"]');
      if (input) input.value = line.quantity;

      const priceEl = item.querySelector('[data-cart-item-price]');
      if (priceEl) priceEl.textContent = formatMoney(line.final_line_price, moneyFormat);
    });
  };

  const changeLine = (item, quantity) => {
    if (item.dataset.busy === 'true') return;
    item.dataset.busy = 'true';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.dataset.lineKey, quantity }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then((cart) => applyCartState(cart))
      .catch(() => window.location.reload())
      .finally(() => { item.dataset.busy = 'false'; });
  };

  items.forEach((item) => {
    const control = item.querySelector('[data-quantity]');
    const input = control?.querySelector('input[type="number"]');

    control?.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!input) return;
        const change = Number(button.dataset.quantityChange);
        const minimum = Number(input.min || 0);
        const next = Math.max(minimum, Number(input.value || minimum) + change);
        changeLine(item, next);
      });
    });

    item.querySelector('[data-cart-remove]')?.addEventListener('click', (event) => {
      event.preventDefault();
      changeLine(item, 0);
    });
  });
}

function initSizeGuideLinks() {
  document.querySelectorAll('[data-size-guide-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const target = document.getElementById(trigger.dataset.sizeGuideTarget);
      if (!target) return;
      target.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initMenuDrawer();
  initProductForms();
  initCartLineActions();
  initSizeGuideLinks();
});
